import ReactSelect, { GroupBase, StylesConfig, Props } from "react-select";

export const baseCustomStyles: StylesConfig = {
  control: (provided, state) => ({
    ...provided,
    borderRadius: 8,
    backgroundColor: "var(--cream)",
    border: "solid 1px var(--foam)",
    // for now we aren't typing custom props, so we need to cast
    // selectProps to any
    ...((state?.selectProps as any)?.isInvalid && {
      // for formik
      border: "solid 2px var(--watermelon)",
      "&:focus": {
        borderColor: "#ea526f",
        boxShadow: "0 0 0 0.2rem rgba(234, 82, 111, 0.25)",
      },
    }),
  }),
  indicatorSeparator: () => ({}),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: "var(--husk)",
    "&:hover": {
      color: "var(--husk)",
    },
    ...(state?.selectProps?.menuIsOpen && { transform: "rotate(180deg)" }),
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: 16,
    color: "var(--rock)",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "4px 6px",
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: 16,
    fontWeight: 500,
    color: "var(--husk)",
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: 9,
    boxShadow: "0 8px 16px 0 rgba(48, 38, 45, 0.08)",
    border: "solid 1px var(--foam)",
    backgroundColor: "var(--cream)",
    overflow: "hidden", // ensure scrollbar doesn't overflow rounded corners
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: 16,
    fontWeight: 500,
    color: "var(--husk)",
    // N.B. order of focus and selected styles matters - selected must come after, so it
    // takes precedence
    "&:active": {
      backgroundColor: "var(--sand)", // on click and hold state
    },
    ...(state?.isFocused && { backgroundColor: "var(--darkFoam)" }),
    ...(state?.isSelected && { backgroundColor: "var(--lightSea)" }),
  }),
  groupHeading: (provided) => ({
    ...provided,
    letterSpacing: 1,
  }),

  menuPortal: (provided, state) => ({
    ...provided,
    ...(state?.position === "fixed" && {
      left: "var(--horizontal-padding)",
    }),
  }),
};

export const darkSelectVariantCustomStyles: StylesConfig = {
  // override control style func, so we can change the background colour and
  // border colour other styles are the same
  control: (provided, state) => ({
    ...provided,
    borderRadius: 8,
    border: "solid 1px var(--darkFoam)",
    backgroundColor: state.menuIsOpen ? "var(--cream)" : "var(--darkFoam)",
  }),
};

type SelectProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> = {
  className?: string;
  customStyles?: StylesConfig<Option, IsMulti, Group>;
  isInvalid?: boolean; // custom prop used for styling, via state.selectProps.isInvalid
  isInScrollContainer?: boolean;
} & Props<Option, IsMulti, Group>;

// N.B. value must be a { label, value } object
function Select<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>({
  placeholder,
  defaultValue,
  options,
  value,
  onChange,
  className,
  customStyles = undefined,
  isDisabled, // N.B. not `disabled`
  isInScrollContainer,
  ...props
}: SelectProps<Option, IsMulti, Group>) {
  return (
    <ReactSelect<Option, IsMulti, Group>
      className={className}
      options={options}
      value={value}
      defaultValue={defaultValue}
      placeholder={placeholder}
      onChange={onChange}
      styles={
        {
          ...baseCustomStyles,
          ...customStyles,
        } as StylesConfig<Option, IsMulti, Group>
      }
      isDisabled={isDisabled}
      // We previously had a bug where the react-select dropdown menu would cause the SideTray body to
      // overflow and scroll. This meant the menu would flow out of sight under the SideTray's button
      // footer.
      // To fix this, we set `menuPosition="fixed"`. However, for some reason react-select then gives
      // the fixed container a completely wrong css `left` value, so we also need to correct that in
      // the custom css above (only if `menuPosition="fixed"` is set).
      // The `isInScrollContainer` prop should only be used when you need to put a react-select component within a
      // container with `overflow: auto/scroll/hidden` (e.g. in the <Body /> of the <SideTray />).
      menuPosition={isInScrollContainer ? "fixed" : undefined}
      // block scroll when the menu position is fixed, so the user can't scroll the container below
      // while the menu is fixed (it looks weird).
      menuShouldBlockScroll={isInScrollContainer}
      {...props}
    />
  );
}

export default Select;
